import {Component, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AdminService} from "../../services/admin.service";
import {ActivatedRoute} from "@angular/router";
import Swal from "sweetalert2";
import {noTrailingSpaceValidator} from "../create-news/create-news.component";

@Component({
  selector: 'app-update-news',
  templateUrl: './update-news.component.html',
  styleUrls: ['./update-news.component.scss']
})
export class UpdateNewsComponent implements OnInit {
  newsId: string = '';
  albanianForm: FormGroup;
  englishForm: FormGroup;
  selectionForm: FormGroup;
  publishDateForm: FormGroup;
  isMainPostForm: FormGroup;
  videoUrlsForm: FormGroup;
  categories: any[] = [];
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private activatedRoute: ActivatedRoute
  ) {
    this.albanianForm = this.fb.group({
      title: ['', [Validators.required, noTrailingSpaceValidator]],
      description: ['', Validators.required]
    });

    this.englishForm = this.fb.group({
      title: ['', noTrailingSpaceValidator],
      description: ['']
    });

    this.selectionForm = this.fb.group({
      categoryId: [null],
      subCategoryId: [null],
      nestedSubCategoryId: [null]
    });

    this.publishDateForm = this.fb.group({
      date: ['', Validators.required],
      time: ['', Validators.required],
    });

    this.isMainPostForm = this.fb.group({
      isMainPost: [''],
      status: ['', Validators.required]
    });

    this.videoUrlsForm = this.fb.group({
      videoUrls: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadCategories();
    this.activatedRoute.paramMap.subscribe(params => {
      this.newsId = params.get('id') || '';
      if (this.newsId) {
        this.loadNewsData();
      }
    });

    this.selectionForm.get('categoryId')?.valueChanges.subscribe(() => {
      this.selectionForm.patchValue({subCategoryId: null, nestedSubCategoryId: null});
    });
    this.selectionForm.get('subCategoryId')?.valueChanges.subscribe(() => {
      this.selectionForm.patchValue({nestedSubCategoryId: null});
    });
  }

  loadCategories(): void {
    this.adminService.getAllCategoriesWithTopLevel().subscribe((res) => {
      this.categories = res;
    });
  }

  loadNewsData(): void {
    this.adminService.getNewsById(this.newsId).subscribe((news: any) => {
      this.albanianForm.patchValue({
        title: news.alTitle,
        description: news.alDescription,
      });

      this.englishForm.patchValue({
        title: news.enTitle,
        description: news.enDescription,
      });

      this.selectionForm.patchValue({
        categoryId: news.category?.id || null,
        subCategoryId: news.subcategory?.id || null,
        nestedSubCategoryId: news.childSubcategory?.id || null,
      });

      const publishedDate = new Date(news.publishedDate);
      const pad = (n: number) => n.toString().padStart(2, '0');
      this.publishDateForm.patchValue({
        date: `${publishedDate.getFullYear()}-${pad(publishedDate.getMonth() + 1)}-${pad(publishedDate.getDate())}`,
        time: `${pad(publishedDate.getHours())}:${pad(publishedDate.getMinutes())}`,
      });

      this.isMainPostForm.patchValue({
        isMainPost: news.isMainPost,
        status: news.status
      });

      this.videoUrls.clear();
      if (news.videoUrls && news.videoUrls.length > 0) {
        news.videoUrls.forEach((url: string) => this.addVideoUrl(url));
      } else {
        this.addVideoUrl();
      }
    });
  }

  get videoUrls(): FormArray {
    return this.videoUrlsForm.get('videoUrls') as FormArray;
  }

  addVideoUrl(value: string = ''): void {
    this.videoUrls.push(this.fb.control(value));
  }

  removeVideoUrl(index: number): void {
    if (this.videoUrls.length > 1) {
      this.videoUrls.removeAt(index);
    }
  }

  getSubCategories() {
    const categoryId = this.selectionForm.get('categoryId')?.value;
    const category = this.categories.find((c) => c.id === categoryId);
    return category ? category.subCategories : [];
  }

  getNestedSubCategories() {
    const subCategoryId = this.selectionForm.get('subCategoryId')?.value;
    const subCategories = this.getSubCategories();
    const subCategory = subCategories.find((sub: any) => sub.id === subCategoryId);
    return subCategory ? subCategory.subCategories : [];
  }

  onSubmit(): void {
    if (this.albanianForm.valid && this.englishForm.valid && this.selectionForm.valid) {
      this.isLoading = true;

      const dateValue = this.publishDateForm.get('date')?.value;
      const timeValue = this.publishDateForm.get('time')?.value;
      const [hours, minutes] = timeValue.split(':');

      const publishedDate = new Date(dateValue);
      publishedDate.setHours(Number(hours), Number(minutes));

      const updatedData = {
        alTitle: this.albanianForm.get('title')?.value,
        alDescription: this.albanianForm.get('description')?.value,
        enTitle: this.englishForm.get('title')?.value,
        enDescription: this.englishForm.get('description')?.value,
        categoryId: this.selectionForm.get('categoryId')?.value,
        subcategoryId: this.selectionForm.get('subCategoryId')?.value,
        childSubcategoryId: this.selectionForm.get('nestedSubCategoryId')?.value,
        publishedDate: publishedDate.toISOString(),
        isMainPost: this.isMainPostForm.get('isMainPost')?.value,
        status: this.isMainPostForm.get('status')?.value,
        videoUrls: this.videoUrls.value.filter((url: string) => url.trim() !== '')
      };

      this.adminService.updateNews(this.newsId, updatedData).subscribe(() => {
        Swal.fire({
          title: 'Success!',
          text: 'News uploaded successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          console.log('Good.')
        });
      }, error => {
        console.error(error)
      });
    }
  }

}
